import { useState, useCallback, useRef } from "react";
import { Link, useBlocker, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useAuth } from "../hooks/useAuth";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { useCreateArticle } from "../hooks/useArticles";
import { useArticleCollections } from "../hooks/useArticleCollections";
import { TreeSelect } from "../components/tree-select/TreeSelect";
import { collectionToOption } from "../components/tree-select/treeOptions";
import { apiService } from "../services/api";
import { extractAndUploadBase64Images } from "../lib/articleImageUtils";
import { buildApiUrl } from "../lib/config";
import { getApiErrorMessage } from "../lib/apiErrors";
import type { CreateArticleRequest } from "../types/articles";
import { ArticleStatus, ArticleVisibility } from "../types/articles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { RichTextEditor } from "../components/RichTextEditor";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ArrowLeft, Save, Star, Loader2, Building2 } from "lucide-react";

interface CreateArticleForm {
  title: string;
  summary: string;
  content: string;
  tags: string;
  visibility: number;
  collectionId: string;
  isFeatured: boolean;
}

export function CreateArticle() {
  const { navigateTo, getPath } = useHelpdeskNavigation();
  const { availableOrganizations, currentOrganization } = useAuth();
  const createArticleMutation = useCreateArticle();
  // A title to start from (Search insights' "Write an article" for a search that found nothing)
  const { title: initialTitle } = useSearch({ from: "/helpdesk/knowledge-base/new" });

  const [content, setContent] = useState("");
  // The organization the user is working in, not just the first one they belong to
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    (currentOrganization && availableOrganizations?.some((o) => o.id === currentOrganization.id)
      ? currentOrganization.id
      : availableOrganizations?.[0]?.id) || ""
  );
  const [orgError, setOrgError] = useState<string | null>(null);

  // The organization's knowledge base collections (and sections) to file the article under
  const { data: collections = [], isLoading: collectionsLoading } = useArticleCollections(selectedOrgId || undefined);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    setValue,
    trigger,
    watch,
  } = useForm<CreateArticleForm>({
    defaultValues: {
      title: initialTitle ?? "",
      summary: "",
      content: "",
      tags: "",
      visibility: ArticleVisibility.Public,
      collectionId: "",
      isFeatured: false,
    },
  });

  // Unsaved-changes guard: a new article has no baseline to compare against, so "any non-empty content"
  // or any react-hook-form dirty field counts as dirty. Bypassed once the article is saved: the navigation to it runs
  // right after the save resolves, before this component re-renders, so the guard reads refs - a value captured
  // in the last render would still say "dirty" and ask to discard an article that was just created.
  const isContentDirty = content.trim().length > 0;
  const isFormDirty = isDirty || isContentDirty;
  const isFormDirtyRef = useRef(isFormDirty);
  isFormDirtyRef.current = isFormDirty;
  const savedRef = useRef(false);
  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirtyRef.current && !savedRef.current,
    enableBeforeUnload: () => isFormDirtyRef.current && !savedRef.current,
    withResolver: true,
  });

  const collectionOptions = (Array.isArray(collections) ? collections : []).map(collectionToOption);

  const onSubmit = async (data: CreateArticleForm) => {
    // Validate organization selection
    if (!selectedOrgId) {
      setOrgError("Please select an organization");
      return;
    }
    setOrgError(null);

    // Trigger content validation
    const isContentValid = await trigger("content");
    if (!isContentValid) {
      return;
    }

    // Upload any remaining base64 images in content (e.g. from rich HTML paste)
    const processedContent = await extractAndUploadBase64Images(content, selectedOrgId || undefined);

    const request: CreateArticleRequest = {
      title: data.title,
      content: processedContent,
      summary: data.summary || undefined,
      tags: data.tags || undefined,
      status: ArticleStatus.Draft,
      visibility: data.visibility as ArticleVisibility,
      collectionId: data.collectionId || undefined,
      isFeatured: data.isFeatured,
      organizationId: selectedOrgId,
    };

    try {
      const result = await createArticleMutation.mutateAsync(request);
      if (result) {
        savedRef.current = true;
        navigateTo.article(result.id);
      }
    } catch {
      // Error is handled in the mutation hook
    }
  };

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const response = await apiService.uploadArticleImage(file, selectedOrgId || undefined);
    if (!response.success || !response.data) {
      throw new Error(getApiErrorMessage(response, 'Failed to upload image'));
    }
    return buildApiUrl(`articles/images/${response.data.id}`);
  }, [selectedOrgId]);

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setValue("content", newContent, { shouldValidate: false }); // Prevent validation on every keystroke
  };

  // Register content field with validation
  register("content", {
    required: "Content is required",
    minLength: {
      value: 10,
      message: "Content must be at least 10 characters long",
    },
    validate: () => {
      if (content.trim().length < 10) {
        return "Content must be at least 10 characters long";
      }
      return true;
    },
  });


  return (
    <div className="space-y-4 md:space-y-6">
      {/* Unsaved-changes blocker — prompts before navigating away with a half-written draft.
          Tab-close / reload is handled separately by useBlocker's enableBeforeUnload (native dialog). */}
      <AlertDialog
        open={blocker.status === "blocked"}
        onOpenChange={(open) => { if (!open) blocker.reset?.(); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to this article. If you leave now, your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Stay on page</AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>Discard and leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button variant="outline" size="icon" className="flex-shrink-0" asChild>
            <Link to={getPath.knowledgeBase()} aria-label="Back to the knowledge base">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-foreground">Create Article</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Create a new knowledge base article
            </p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {createArticleMutation.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {createArticleMutation.error?.message || "Failed to create article. Please try again."}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Basic Information */}
            <Card className="p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Article Details</h2>

              <div className="space-y-4">
                {/* Organization Selector - show if user has multiple orgs */}
                {availableOrganizations && availableOrganizations.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      <span className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Organization <span className="text-red-500">*</span>
                      </span>
                    </label>
                    <select
                      value={selectedOrgId}
                      onChange={(e) => {
                        setSelectedOrgId(e.target.value);
                        setOrgError(null);
                        // Reset the collection when the organization changes
                        setValue("collectionId", "");
                      }}
                      className={`w-full px-3 py-2 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                        orgError ? "border-red-500" : "border-input"
                      }`}
                    >
                      {availableOrganizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                    {orgError && (
                      <p className="text-red-500 text-sm mt-1">{orgError}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    {...register("title", {
                      required: "Title is required",
                      validate: (value) =>
                        value.trim().length >= 3 || "Title must be at least 3 characters",
                      maxLength: {
                        value: 255,
                        message: "Title must be 255 characters or less",
                      },
                    })}
                    placeholder="Enter article title"
                    className={errors.title ? "border-red-500" : ""}
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Summary
                  </label>
                  <textarea
                    {...register("summary", {
                      maxLength: {
                        value: 500,
                        message: "Summary must be less than 500 characters",
                      },
                    })}
                    rows={3}
                    placeholder="Brief summary of the article (optional)"
                    className={`w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                      errors.summary ? "border-red-500" : ""
                    }`}
                  />
                  {errors.summary && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.summary.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tags
                  </label>
                  <Input
                    {...register("tags")}
                    placeholder="Enter tags separated by commas"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Separate multiple tags with commas
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Visibility
                  </label>
                  <select
                    {...register("visibility", { valueAsNumber: true })}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={ArticleVisibility.Public}>Public</option>
                    <option value={ArticleVisibility.Internal}>Internal</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Internal articles are only visible to agents
                  </p>
                </div>

                <div>
                  <label htmlFor="collectionId" className="block text-sm font-medium text-foreground mb-2">
                    Collection
                  </label>
                  <TreeSelect
                    id="collectionId"
                    options={collectionOptions}
                    value={watch("collectionId") || null}
                    onChange={(id) => setValue("collectionId", id ?? "", { shouldDirty: true })}
                    placeholder={collectionsLoading ? "Loading collections..." : "No collection"}
                    noneLabel="No collection"
                    searchPlaceholder="Search collections..."
                    emptyMessage="No collections yet - add them in Org Settings"
                    disabled={collectionsLoading}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Where the article appears in the help center
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    {...register("isFeatured")}
                    id="isFeatured"
                    className="rounded border-input text-primary focus:ring-ring"
                  />
                  <label
                    htmlFor="isFeatured"
                    className="text-sm font-medium text-foreground flex items-center gap-2"
                  >
                    <Star className="h-4 w-4" />
                    Featured Article
                  </label>
                </div>
              </div>
            </Card>

            {/* Content Editor */}
            <Card className="p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                Content <span className="text-red-500">*</span>
              </h2>

              <RichTextEditor
                content={content}
                onChange={handleContentChange}
                onImageUpload={handleImageUpload}
                placeholder="Start writing your article content..."
                className={errors.content ? "border-red-500" : ""}
              />
              {errors.content && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.content.message}
                </p>
              )}
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                type="submit"
                disabled={isSubmitting || createArticleMutation.isPending}
                className="w-full sm:flex-1"
              >
                {isSubmitting || createArticleMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Article
                  </>
                )}
              </Button>

              <Link to={getPath.knowledgeBase()} className="w-full sm:flex-1">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={isSubmitting || createArticleMutation.isPending}
                >
                  Cancel
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}